import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { resolveClientFeatures } from "@/lib/resolveClientFeatures";
import type { ClientFeatureMap } from "@/lib/resolveClientFeatures";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import MessageThread from "@/components/feature/MessageThread";
import ContractCard, { PortalContract } from "./components/ContractCard";
import SummaryCards from "./components/SummaryCards";
import JobTimelineSection from "./components/JobTimelineSection";
import type { PortalJob, JobStep, PortalJobDelivery } from "./components/JobTimelineSection";
import PaymentsSection from "./components/PaymentsSection";
import type { PortalPayment } from "./components/PaymentsSection";
import PhotoSelectionSection from "./components/PhotoSelectionSection";
import type { JobPhoto } from "./components/PhotoSelectionSection";
import DeliveriesSection from "./components/DeliveriesSection";
import type { JobDelivery } from "./components/DeliveriesSection";
import MediaPackagesSection from "./components/MediaPackagesSection";
import type { PortalMediaPackage } from "./components/JobTimelineSection";
import AppointmentsSection from "./components/AppointmentsSection";
import type { PortalAppointment } from "./components/AppointmentsSection";
import ActiveFeaturesWidget from "./components/ActiveFeaturesWidget";
import EventRoutesSection from "./components/EventRoutesSection";
import type { PortalEventRoute, PortalRouteStop } from "./components/EventRoutesSection";
import Swal from "sweetalert2";

/* ─── Types ─────────────────────────────────────────────────── */
interface TokenRow {
  id: string;
  tenant_id: string;
  client_id: string;
  token: string;
  short_code: string;
  status: string;
  expires_at: string | null;
}

interface Client {
  id: string;
  full_name: string;
  email: string;
  phone: string;
}

interface Branding {
  business_name: string;
  logo_url: string | null;
  primary_color: string | null;
}

interface PortalData {
  token: TokenRow;
  client: Client;
  branding: Branding;
  contracts: PortalContract[];
  payments: PortalPayment[];
  jobs: PortalJob[];
  photos: JobPhoto[];
  deliveries: JobDelivery[];
  steps: JobStep[];
  mediaPackages: PortalMediaPackage[];
  appointments: PortalAppointment[];
  eventRoutes: PortalEventRoute[];
  features: ClientFeatureMap;
  hasInvitation: boolean;
  hasSignatureBook: boolean;
}

/* ─── Access denied ─────────────────────────────────────────── */
function AccessDenied({ reason }: { reason: string }) {
  return (
    <div className="min-h-screen bg-[#f8f7f5] flex items-center justify-center px-4">
      <div className="text-center max-w-sm mx-auto">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-5">
          <i className="ri-lock-line text-red-500 text-2xl"></i>
        </div>
        <h1 className="text-[#0f172a] text-xl font-bold mb-2">Access Denied</h1>
        <p className="text-slate-500 text-sm leading-relaxed">{reason}</p>
      </div>
    </div>
  );
}

/* ─── Section header ────────────────────────────────────────── */
function SectionTitle({ icon, label }: { icon: string; label: string }) {
  return (
    <h2 className="text-[#0f172a] font-bold text-base flex items-center gap-2 mb-4">
      <span className="w-5 h-5 flex items-center justify-center text-[#9a7840]">
        <i className={icon}></i>
      </span>
      {label}
    </h2>
  );
}

/* ─── Main ──────────────────────────────────────────────────── */
export default function ClientPortal() {
  const { token: paramToken } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [networkError, setNetworkError] = useState(false);
  const [data, setData] = useState<PortalData | null>(null);
  const [updatingPhotoId, setUpdatingPhotoId] = useState<string | null>(null);

  const fetchPortalData = useCallback(async () => {
    setNetworkError(false);
    try {
      if (!paramToken) {
        setError("No access token provided.");
        setLoading(false);
        return;
      }

      /* ── 1. Validate token ── */
      const { data: tokenRow, error: tokenErr } = await supabase
        .from("client_access_tokens")
        .select("*")
        .eq("token", paramToken)
        .maybeSingle();

      if (tokenErr || !tokenRow) {
        setError("This portal link is invalid. Please contact your photographer for a new link.");
        setLoading(false);
        return;
      }
      if (tokenRow.status !== "active") {
        setError("This portal link has been revoked or deactivated. Please contact your photographer.");
        setLoading(false);
        return;
      }
      if (tokenRow.expires_at && new Date(tokenRow.expires_at) < new Date()) {
        setError("This portal link has expired. Please contact your photographer for a new access link.");
        setLoading(false);
        return;
      }

      /* ── 2. Client ── */
      const { data: clientRow, error: clientErr } = await supabase
        .from("clients")
        .select("id, full_name, email, phone")
        .eq("id", tokenRow.client_id)
        .maybeSingle();

      if (clientErr || !clientRow) {
        setError("Client information not found. Please contact your photographer.");
        setLoading(false);
        return;
      }

      /* ── 3. Parallel fetches ── */
      const [
        contractsRes,
        paymentsRes,
        brandingRes,
        jobsRes,
        featuresRes,
        invRes,
        sbRes,
      ] = await Promise.all([
        supabase
          .from("contracts")
          .select("id, contract_number, title, status, event_date, event_location, package_name, total_amount, deposit_amount, balance_amount, rendered_content, signature_status, client_signature, signed_at, payment_link, payment_status")
          .eq("client_id", clientRow.id)
          .order("created_at", { ascending: false }),
        supabase
          .from("payments")
          .select("id, amount, method, payment_date, reference, status")
          .eq("client_id", clientRow.id)
          .order("created_at", { ascending: false }),
        supabase
          .from("saas_tenant_branding")
          .select("business_name, logo_url, primary_color")
          .eq("tenant_id", tokenRow.tenant_id)
          .maybeSingle(),
        supabase
          .from("jobs")
          .select("id, title, job_type, status, workflow_status, shoot_date, location, photo_selection_status, photo_selection_submitted_at, delivery_status, delivered_at, editing_started_at, completed_at")
          .eq("client_id", clientRow.id)
          .eq("tenant_id", tokenRow.tenant_id)
          .order("shoot_date", { ascending: false }),
        resolveClientFeatures(tokenRow.tenant_id, clientRow.id),
        supabase
          .from("digital_invitations")
          .select("id, is_published")
          .eq("tenant_id", tokenRow.tenant_id)
          .eq("client_id", clientRow.id)
          .maybeSingle(),
        supabase
          .from("signature_books")
          .select("id, status")
          .eq("tenant_id", tokenRow.tenant_id)
          .eq("client_id", clientRow.id)
          .maybeSingle(),
      ]);

      const features = featuresRes;
      const jobs = (jobsRes.data ?? []) as PortalJob[];
      const jobIds = jobs.map((j) => j.id);

      let photoRows: JobPhoto[] = [];
      let deliveryRows: JobDelivery[] = [];
      let stepRows: JobStep[] = [];
      let eventRouteRows: PortalEventRoute[] = [];

      if (jobIds.length > 0) {
        const [photosRes, deliveriesRes, stepsRes, routesRes] = await Promise.all([
          supabase
            .from("job_photos")
            .select("id, job_id, photo_url, thumbnail_url, filename, category, is_selected, is_favorite, selection_note")
            .in("job_id", jobIds)
            .eq("client_id", clientRow.id)
            .order("sort_order", { ascending: true }),
          supabase
            .from("job_deliveries")
            .select("id, job_id, delivery_type, title, description, file_url, thumbnail_url, expires_at, is_active")
            .in("job_id", jobIds)
            .eq("client_id", clientRow.id)
            .eq("is_active", true)
            .order("created_at", { ascending: false }),
          supabase
            .from("job_steps")
            .select("id, job_id, step_key, title, status, sort_order, due_date, completed_at")
            .in("job_id", jobIds)
            .order("sort_order", { ascending: true }),
          supabase
            .from("event_routes")
            .select("id, title, event_date, notes")
            .in("job_id", jobIds)
            .eq("tenant_id", tokenRow.tenant_id)
            .order("created_at", { ascending: true }),
        ]);
        photoRows = (photosRes.data ?? []) as JobPhoto[];
        deliveryRows = (deliveriesRes.data ?? []) as JobDelivery[];
        stepRows = (stepsRes.data ?? []) as JobStep[];

        /* Fetch stops for each route */
        const rawRoutes = (routesRes.data ?? []) as { id: string; title: string; event_date: string | null; notes: string | null }[];
        if (rawRoutes.length > 0) {
          const routeIds = rawRoutes.map((r) => r.id);
          const { data: stopsData } = await supabase
            .from("event_route_stops")
            .select("id, route_id, label, address, place_type, time_scheduled, duration_min, notes, sort_order")
            .in("route_id", routeIds)
            .eq("tenant_id", tokenRow.tenant_id)
            .order("sort_order", { ascending: true });
          const allStops = (stopsData ?? []) as PortalRouteStop[];
          eventRouteRows = rawRoutes.map((r) => ({
            ...r,
            stops: allStops.filter((s) => s.route_id === r.id),
          }));
        }
      }

      const [mediaPkgRes, apptRes] = await Promise.all([
        supabase
          .from("media_packages")
          .select("id, job_id, package_type, title, cover_url, video_url, download_url, public_token, is_published")
          .eq("client_id", clientRow.id)
          .eq("is_published", true)
          .order("created_at", { ascending: false }),
        supabase
          .from("appointments")
          .select("id, title, appointment_type, status, start_at, end_at, location")
          .eq("tenant_id", tokenRow.tenant_id)
          .eq("client_id", clientRow.id)
          .eq("show_in_portal", true)
          .order("start_at", { ascending: true }),
      ]);

      const brandingData = brandingRes.data;
      const branding: Branding = {
        business_name: brandingData?.business_name ?? "Photography Studio",
        logo_url: brandingData?.logo_url ?? null,
        primary_color: brandingData?.primary_color ?? "#C8A96E",
      };

      setData({
        token: tokenRow as TokenRow,
        client: clientRow as Client,
        branding,
        contracts: (contractsRes.data ?? []) as PortalContract[],
        payments: (paymentsRes.data ?? []) as PortalPayment[],
        jobs,
        photos: photoRows,
        deliveries: deliveryRows,
        steps: stepRows,
        mediaPackages: (mediaPkgRes.data ?? []) as PortalMediaPackage[],
        appointments: (apptRes.data ?? []) as PortalAppointment[],
        eventRoutes: eventRouteRows,
        features,
        hasInvitation: !!invRes.data,
        hasSignatureBook: features.signature_book && !!sbRes.data,
      });
      setLoading(false);
    } catch {
      setNetworkError(true);
      setLoading(false);
    }
  }, [paramToken]);

  useEffect(() => {
    fetchPortalData();
  }, [fetchPortalData]);

  /* ─── Photo handlers ────────────────────────────────────── */
  const handlePhotoToggle = async (
    photoId: string,
    field: "is_selected" | "is_favorite",
    currentVal: boolean,
  ) => {
    if (!data) return;
    setUpdatingPhotoId(photoId);
    const { data: updated, error: upErr } = await supabase
      .from("job_photos")
      .update({ [field]: !currentVal, updated_at: new Date().toISOString() })
      .eq("id", photoId)
      .eq("tenant_id", data.token.tenant_id)
      .select("id, job_id, photo_url, thumbnail_url, filename, category, is_selected, is_favorite, selection_note")
      .maybeSingle();
    if (!upErr && updated) {
      setData((prev) =>
        prev ? { ...prev, photos: prev.photos.map((p) => (p.id === photoId ? (updated as JobPhoto) : p)) } : prev,
      );
    }
    setUpdatingPhotoId(null);
  };

  const handleNoteUpdate = async (photoId: string, note: string) => {
    if (!data) return;
    await supabase
      .from("job_photos")
      .update({ selection_note: note || null, updated_at: new Date().toISOString() })
      .eq("id", photoId)
      .eq("tenant_id", data.token.tenant_id);
    setData((prev) =>
      prev
        ? { ...prev, photos: prev.photos.map((p) => (p.id === photoId ? { ...p, selection_note: note || null } : p)) }
        : prev,
    );
  };

  const handleSubmitSelection = async () => {
    if (!data) return;
    const result = await Swal.fire({
      title: "Submit photo selection?",
      text: "Your selected photos will be sent to the photographer. You won't be able to change them after submitting.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Submit",
      cancelButtonText: "Cancel",
      background: "#fff",
      color: "#1e293b",
      confirmButtonColor: "#C8A96E",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed) return;

    const now = new Date().toISOString();
    const jobIds = data.jobs.map((j) => j.id);
    const { error } = await supabase
      .from("jobs")
      .update({ photo_selection_status: "submitted", photo_selection_submitted_at: now, updated_at: now })
      .in("id", jobIds)
      .eq("tenant_id", data.token.tenant_id);

    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#fff", color: "#1e293b" });
      return;
    }

    try {
      const { data: admins } = await supabase
        .from("saas_tenant_users")
        .select("user_id")
        .eq("tenant_id", data.token.tenant_id);
      if (admins && admins.length > 0) {
        await supabase.from("notifications").insert(
          admins.map((a) => ({
            tenant_id: data.token.tenant_id,
            user_id: a.user_id,
            type: "info",
            title: "Photo selection submitted",
            message: `${data.client.full_name} has submitted their photo selection.`,
          })),
        );
      }
    } catch {
      // ignore notification failure
    }

    setData((prev) =>
      prev ? { ...prev, jobs: prev.jobs.map((j) => ({ ...j, photo_selection_status: "submitted" })) } : prev,
    );

    Swal.fire({
      title: "Submitted!",
      text: "Your photo selection has been sent to the photographer.",
      icon: "success",
      timer: 2500,
      showConfirmButton: false,
      background: "#fff",
      color: "#1e293b",
      iconColor: "#C8A96E",
    });
  };

  /* ─── Render states ─────────────────────────────────────── */
  if (loading) return <LoadingState fullPage text="Loading your portal…" />;
  if (networkError)
    return (
      <ErrorState
        fullPage
        title="Couldn't reach the server"
        message="Please check your internet connection and try again."
        onRetry={() => window.location.reload()}
      />
    );
  if (error || !data) return <AccessDenied reason={error ?? "Unknown error."} />;

  const {
    token: tok,
    client,
    branding,
    contracts,
    payments,
    jobs,
    photos,
    deliveries,
    steps,
    mediaPackages,
    appointments,
    eventRoutes,
    features,
    hasInvitation,
    hasSignatureBook,
  } = data;

  /* ─── Derived flags ──────────────────────────────────────── */
  const CONFIRMED_STATUSES = ["confirmed", "paid", "completed"];
  const confirmedPayments = payments.filter((p) => CONFIRMED_STATUSES.includes(p.status));
  const totalOwed = contracts.reduce((a, b) => a + Number(b.total_amount), 0);
  const totalPaid = confirmedPayments.reduce((a, b) => a + Number(b.amount), 0);
  const totalBalance = contracts.reduce((a, b) => a + Number(b.balance_amount), 0);

  const hasContracts = contracts.length > 0;
  const hasPayments = payments.length > 0;
  const hasJobs = jobs.length > 0;
  const hasPhotos = photos.length > 0;
  const hasDeliveries = deliveries.length > 0;
  const hasMediaPackages = mediaPackages.length > 0;
  const hasAppointments = appointments.length > 0;

  const selectedCount = photos.filter((p) => p.is_selected).length;
  const allJobsSubmitted = jobs.every(
    (j) => j.photo_selection_status === "submitted" || j.photo_selection_status === "reviewed",
  );
  const anyJobsSelectable = jobs.some(
    (j) => !j.photo_selection_status || j.photo_selection_status === "not_started" || j.photo_selection_status === "in_progress",
  );

  /* ─── Show photo selection section ───────────────────────── */
  const showPhotoSelection = features.photo_selection && hasJobs && hasPhotos;
  /* ─── Show deliveries section ────────────────────────────── */
  const showDeliveries = features.final_deliveries && hasDeliveries;
  /* ─── Show media section ─────────────────────────────────── */
  const showMedia = features.dvd_online && hasMediaPackages;
  /* ─── Show appointments section ──────────────────────────── */
  const showAppointments = features.appointments && hasAppointments;

  /* ─── Brand color ────────────────────────────────────────── */
  const brandColor = branding.primary_color ?? "#C8A96E";

  return (
    <div className="min-h-screen bg-[#f8f7f5]">
      {/* ── Header ── */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-3">
          {/* Brand */}
          <div className="flex items-center gap-3 min-w-0">
            {branding.logo_url ? (
              <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0 bg-slate-100">
                <img
                  src={branding.logo_url}
                  alt={branding.business_name}
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${brandColor}20` }}
              >
                <i className="ri-camera-line text-sm" style={{ color: brandColor }}></i>
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[#0f172a] font-semibold text-sm truncate">{branding.business_name}</p>
              <p className="text-slate-400 text-xs">Client Portal</p>
            </div>
          </div>

          {/* Token badge */}
          <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg px-3 py-1.5 border border-slate-200 shrink-0">
            <i className="ri-shield-check-line text-emerald-500 text-xs"></i>
            <span className="text-slate-400 text-xs font-mono">{tok.short_code}</span>
          </div>
        </div>
      </header>

      {/* ── Content ── */}
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-10 space-y-6 md:space-y-8">

        {/* ① Welcome */}
        <div className="pt-2">
          <h1 className="text-[#0f172a] text-xl md:text-2xl font-bold mb-1.5">
            Hello, {client.full_name.split(" ")[0]} 👋
          </h1>
          <p className="text-slate-500 text-sm">
            Welcome to your personal studio portal — everything about your booking in one place.
          </p>
        </div>

        {/* ② Active features widget */}
        <ActiveFeaturesWidget
          features={features}
          portalToken={tok.token}
          hasInvitation={hasInvitation}
          hasSignatureBook={hasSignatureBook}
          brandColor={brandColor}
        />

        {/* ③ Financial summary */}
        {hasContracts && (
          <SummaryCards
            totalOwed={totalOwed}
            totalPaid={totalPaid}
            totalBalance={totalBalance}
          />
        )}

        {/* ④ Contracts */}
        {hasContracts && (
          <section>
            <SectionTitle icon="ri-file-text-line" label="Your Contracts" />
            <div className="space-y-3">
              {contracts.map((c) => (
                <ContractCard
                  key={c.id}
                  contract={c}
                  portalToken={tok.token}
                  clientId={tok.client_id}
                  tenantName={branding.business_name}
                  clientName={client.full_name}
                  onSigned={(cid) =>
                    setData((prev) =>
                      prev
                        ? {
                            ...prev,
                            contracts: prev.contracts.map((x) =>
                              x.id === cid ? { ...x, signature_status: "signed", status: "signed" } : x,
                            ),
                          }
                        : prev,
                    )
                  }
                />
              ))}
            </div>
          </section>
        )}

        {/* ⑤ Payments — only if there are confirmed payments */}
        {hasPayments && <PaymentsSection payments={payments} brandColor={brandColor} />}

        {/* ⑥ Job Timeline */}
        {hasJobs && (
          <JobTimelineSection
            jobs={jobs}
            steps={steps}
            mediaPackages={mediaPackages}
            deliveries={deliveries as PortalJobDelivery[]}
            hasSignatureBook={hasSignatureBook}
            portalToken={tok.token}
            brandColor={brandColor}
            features={{
              photo_selection: features.photo_selection,
              final_deliveries: features.final_deliveries,
              dvd_online: features.dvd_online,
              signature_book: features.signature_book,
            }}
          />
        )}

        {/* ⑦ Event Routes — only if there are routes with stops */}
        {eventRoutes.length > 0 && (
          <EventRoutesSection
            routes={eventRoutes}
            brandColor={brandColor}
          />
        )}

        {/* ⑧ Invitation quick link — only if feature active */}
        {features.digital_invitation && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${brandColor}18` }}
            >
              <i className="ri-mail-send-line text-lg" style={{ color: brandColor }}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[#0f172a] font-semibold text-sm">Digital Invitation</p>
              <p className="text-slate-400 text-xs mt-0.5">
                {hasInvitation
                  ? "Your invitation is ready — edit design, manage guests and share."
                  : "Contact your photographer to activate your digital invitation."}
              </p>
            </div>
            {hasInvitation && (
              <Link
                to={`/portal/${tok.token}/invitation`}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all cursor-pointer whitespace-nowrap shrink-0 hover:opacity-90"
                style={{ backgroundColor: brandColor }}
              >
                Open
              </Link>
            )}
          </div>
        )}

        {/* ⑨ Signature Book quick link — only if addon active AND book exists */}
        {hasSignatureBook && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${brandColor}18` }}
            >
              <i className="ri-book-open-line text-lg" style={{ color: brandColor }}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[#0f172a] font-semibold text-sm">Signature Book</p>
              <p className="text-slate-400 text-xs mt-0.5">
                View guest messages and download the PDF.
              </p>
            </div>
            <Link
              to={`/portal/${tok.token}/signature-book`}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all cursor-pointer whitespace-nowrap shrink-0 hover:opacity-90"
              style={{ backgroundColor: brandColor }}
            >
              Open
            </Link>
          </div>
        )}

        {/* ⑩ Photo Selection — only if active + has photos */}
        {showPhotoSelection && (
          <section id="photo-selection" className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-[#0f172a] font-bold text-base flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center" style={{ color: brandColor }}>
                    <i className="ri-image-2-line"></i>
                  </span>
                  Photo Selection
                </h2>
                {allJobsSubmitted ? (
                  <span className="bg-amber-50 border border-amber-200 text-amber-600 text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                    <i className="ri-time-line text-[10px]"></i>
                    Submitted — awaiting review
                  </span>
                ) : (
                  <span className="bg-sky-50 border border-sky-200 text-sky-600 text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                    <i className="ri-edit-line text-[10px]"></i>
                    In progress · {selectedCount} selected
                  </span>
                )}
              </div>
              {selectedCount > 0 && anyJobsSelectable && !allJobsSubmitted && (
                <button
                  onClick={handleSubmitSelection}
                  className="flex items-center gap-1.5 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-all cursor-pointer whitespace-nowrap hover:opacity-90"
                  style={{ backgroundColor: brandColor }}
                >
                  <i className="ri-send-plane-line"></i>
                  Submit Selection
                </button>
              )}
            </div>
            <PhotoSelectionSection
              jobs={jobs}
              photos={photos}
              updatingPhotoId={updatingPhotoId}
              onPhotoToggle={handlePhotoToggle}
              onNoteUpdate={handleNoteUpdate}
            />
          </section>
        )}

        {/* ⑪ Final Deliveries — only if active + has deliveries */}
        {showDeliveries && (
          <section id="deliveries" className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
            <DeliveriesSection deliveries={deliveries} brandColor={brandColor} />
          </section>
        )}

        {/* ⑫ Media Packages — only if active + has packages */}
        {showMedia && <MediaPackagesSection mediaPackages={mediaPackages} />}

        {/* ⑬ Appointments — only if active + has appointments */}
        {showAppointments && <AppointmentsSection appointments={appointments} brandColor={brandColor} />}

        {/* ⑭ Messages — always visible */}
        <section>
          <SectionTitle icon="ri-chat-3-line" label="Messages" />
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" style={{ height: "420px" }}>
            <MessageThread
              tenantId={tok.tenant_id}
              clientId={tok.client_id}
              senderType="client"
              clientName={client.full_name}
            />
          </div>
        </section>

        {/* ⑮ Contact footer */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Contact Your Studio</p>
          <div className="flex items-center gap-2 text-slate-600 text-sm font-medium">
            <span className="w-4 h-4 flex items-center justify-center" style={{ color: brandColor }}>
              <i className="ri-building-line text-xs"></i>
            </span>
            {branding.business_name}
          </div>
          <p className="text-slate-400 text-xs mt-1.5">
            Use the messages section above to contact your photographer directly.
          </p>
        </div>

        {/* ⑯ Powered by footer */}
        <div className="text-center py-4 border-t border-slate-200">
          <p className="text-slate-400 text-xs">
            Powered by{" "}
            <span className="text-[#0f172a] font-semibold tracking-wide">Pixory AI</span>
            {" "}· Smart Business Control for Photographers
          </p>
        </div>
      </div>
    </div>
  );
}